'use client';

import React, {useEffect, useMemo, useState, type ReactNode} from 'react';
import {getMfaApi} from '../../api/mfa-api';
import {MfaVerification} from './MfaVerification';
import {useApiToken} from '@/matic/hooks/useApiClient';
import {useAppContext} from '@/matic/providers/AppProvider';
import {useTenantSettings} from '../../hooks/useTenantSettings';
import {Button} from '@/matic/components/ui/button';
import {hideAppLoader} from '@/matic/lib/utils/hide-app-loader';

// Pre-scoping key. A single global key meant one account's token clobbered
// another's in the same browser profile; read once for migration, then drop.
const LEGACY_MFA_TOKEN_KEY = 'mfa_token';

const mfaTokenKey = (tenantId: string, userId: string) =>
  `mfa_token:${tenantId}:${userId}`;

type MfaGateProps = {
  children: ReactNode;
};

export function MfaGate({children}: MfaGateProps) {
  const [mfaVerified, setMfaVerified] = useState(false);
  const [checking, setChecking] = useState(true);
  const token = useApiToken();
  const {tenantId, userId} = useAppContext();

  const api = useMemo(() => getMfaApi(token), [token]);

  const {data: settings, isLoading, isError, refetch} = useTenantSettings();

  useEffect(() => {
    if (isError && !settings) hideAppLoader();
  }, [isError, settings]);

  const storageKey =
    tenantId && userId ? mfaTokenKey(tenantId, userId) : undefined;

  useEffect(() => {
    if (isLoading || !settings || !storageKey) return;

    if (!settings.mfaRequired) {
      setChecking(false);
      return;
    }

    const scopedToken = localStorage.getItem(storageKey);
    // Fall back to the legacy global key so existing users aren't forced
    // through a fresh email challenge by the move to scoped keys.
    const storedToken =
      scopedToken ?? localStorage.getItem(LEGACY_MFA_TOKEN_KEY);
    if (!storedToken) {
      setChecking(false);
      return;
    }

    const validateToken = async () => {
      try {
        const result = await api.validateMfaToken(storedToken);
        if (result.valid) {
          if (!scopedToken) {
            // The legacy token belongs to this account — promote it.
            localStorage.setItem(storageKey, storedToken);
            localStorage.removeItem(LEGACY_MFA_TOKEN_KEY);
          }

          setMfaVerified(true);
        } else if (scopedToken) {
          // The server says this token isn't ours (or has expired).
          localStorage.removeItem(storageKey);
        } else {
          // The legacy token belonged to a different account.
          localStorage.removeItem(LEGACY_MFA_TOKEN_KEY);
        }
      } catch {
        // Network/transient failure — we can't trust an unvalidated token this
        // session, but the remembered device survives for the next page load.
      } finally {
        setChecking(false);
      }
    };

    void validateToken();
  }, [settings, isLoading, api, storageKey]);

  // Only a first load that failed: a failed background refetch keeps the settings it has.
  if (isError && !settings) {
    return (
      <div
        role="alert"
        className="flex min-h-screen items-center justify-center p-6"
      >
        <div className="max-w-md space-y-4 text-center">
          <h1 className="text-lg font-semibold text-neutral-900">
            Unable to load workspace
          </h1>
          <p className="text-sm text-neutral-600">
            We couldn't verify your workspace's sign-in requirements. Check your
            connection and try again.
          </p>
          <Button
            onClick={() => {
              void refetch();
            }}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading || checking) {
    return (
      <div
        role="status"
        className="flex min-h-screen items-center justify-center text-sm text-neutral-600"
      >
        Checking workspace sign-in requirements…
      </div>
    );
  }

  if (settings?.mfaRequired && !mfaVerified) {
    return (
      <MfaVerification
        onVerified={(mfaToken) => {
          if (storageKey) {
            localStorage.setItem(storageKey, mfaToken);
          }

          setMfaVerified(true);
        }}
      />
    );
  }

  return <>{children}</>;
}
